import type {
  CommentElementProps,
  DynamicFormField,
  JsonDataItem,
  OptionWithOptionalProperties,
  VisibilityCondition,
  VisibilityConditionConfig,
} from '@myosh/odin-components';
import { isDropDownNoSelection, isObject, Tooltip } from '@myosh/odin-components';
import { isEqual } from 'lodash';
import React from 'react';
import type { DropDownValue, OptionBuilderValue, ValueTextType } from '../../../@types/common';
import type { FormWorkflowStepAccessControl } from '../../../@types/form-permissions';
import type {
  CombinedFieldType,
  ExtendedDynamicFormHierarchySettings,
  ExtendedDynamicFormSettings,
  ExtendedDynamicFormWorkflowStep,
} from '../../../@types/forms';
import type { HierarchyDropdownValue } from '../../../@types/hierarchy-fields';
import type { QuestionnaireFieldValue } from '../../../@types/questionnaire';
import type { RecordMutationErrorResponse, RecordResult } from '../../../@types/records';
import i18next from '../../../i18n';
import { formatToDateOnly } from '../../../utils/date-util';
import { findFieldRecursive } from '../../../utils/dynamic-form-utils';
import {
  forceAssert,
  isArrayOfOptionWithOptionalProperties,
  isArrayOfValueTextType,
  isInstanceOfOptionBuilder,
  isInstanceOfValueTextType,
} from '../../../utils/type-guards';
import type { Conflict } from '../conflicts-modal/conflicts-modal.component';

/**
 * Updates the record object 'original' data.
 *
 * The 'original' data represents the Record object as it was received from the API.
 *
 * @param data the updated record data
 * @param originalData the original record data.
 * @param schemaTimeZone the schema time zone
 */
export function updateOriginalData(data: Record<string, unknown>, originalData: RecordResult) {
  const dataKeys = Object.keys(data);
  const originalDataKeys = Object.keys(originalData);
  const originalFieldsDataKeys = Object.keys(originalData.fields);

  for (let i = 0, length = dataKeys.length; i < length; i++) {
    if (originalFieldsDataKeys.includes(dataKeys[i])) {
      _setFieldValue(data, originalData, dataKeys[i]);
    } else if (dataKeys[i] === 'hierarchyValues') {
      originalData.hierarchies = _createHierarchiesObject(data[dataKeys[i]] as Record<string, HierarchyDropdownValue>);
    } else if (dataKeys[i] === 'files') {
      originalData.files = forceAssert<Array<string>>(data[dataKeys[i]]);
    } else if (dataKeys[i] === 'images') {
      originalData.images = forceAssert<Array<string>>(data[dataKeys[i]]);
    } else if (!originalDataKeys.includes(dataKeys[i])) {
      _setFieldValue(data, originalData, dataKeys[i]);
    }
  }
}

function _setFieldValue(
  data: Record<string, unknown>,
  originalData: RecordResult,
  key: string,
  fieldsToEliminate?: Array<string>
) {
  const dataItem = data[key];
  if (isInstanceOfValueTextType(dataItem)) {
    originalData.fields[key] = {
      id: parseInt(dataItem.value as string),
      value: dataItem.text,
    };
  } else if (
    Array.isArray(dataItem) &&
    dataItem.length > 0 &&
    (isArrayOfOptionWithOptionalProperties(dataItem) || isArrayOfValueTextType(dataItem))
  ) {
    originalData.fields[key] = []; // reset original value
    for (let i = 0; i < dataItem.length; i++) {
      let currentItem;
      if (isArrayOfOptionWithOptionalProperties(dataItem)) {
        currentItem = forceAssert<OptionWithOptionalProperties>(dataItem[i]);
        forceAssert<Array<OptionBuilderValue>>(originalData.fields[key])[i] = {
          id: typeof currentItem.value === 'string' ? parseInt(currentItem.value) : (currentItem.value as number),
          value: currentItem.text,
          archivedOption: currentItem.archivedOption ?? false,
          defaultOption: currentItem.defaultOption ?? false,
          position: currentItem.position ?? 0,
        };
      } else if (isArrayOfValueTextType(dataItem)) {
        currentItem = forceAssert<ValueTextType>(dataItem[i]);
        forceAssert<Array<{ id: number; value: string }>>(originalData.fields[key])[i] = {
          id: typeof currentItem.value === 'string' ? parseInt(currentItem.value) : currentItem.value,
          value: currentItem.text,
        };
      }
    }
  } else if (isDropDownNoSelection(dataItem)) {
    originalData.fields[key] = null;
  } else if (dataItem instanceof Date) {
    originalData.fields[key] = formatToDateOnly(dataItem);
  } else if (!fieldsToEliminate || (fieldsToEliminate && !fieldsToEliminate.some((_key) => _key === key))) {
    originalData.fields[key] = data[key];
  }
}

/**
 * Creates a new Record object that will be sent to the backend for persisting.
 *
 * @param formId {number} - the id of the form.
 * @param status {string} the status of the record defined by the workflow steps
 * @param data {Record<string, unknown>} the record data.
 * @param schemaTimeZone {string} the schema time zone
 *
 * @returns A record object ready to be sent to the backend.
 */
export function createSaveData(formId: number, status: string, data: Record<string, unknown>) {
  const record: RecordResult = {
    formId,
    status,
    hierarchies: {},
    fields: {},
  };

  return _createRecordObject(record, data, Object.keys(data));
}

/**
 * Creates a patch Record object that will be sent to the backend for persisting.
 *
 * @param formId {number} - the id of the form.
 * @param status {string} the status of the record defined by the workflow steps
 * @param data {Record<string, unknown>} the record data.
 * @param dirtyFields {Record<string, unknown>} the form fields that have been modified. Used when updating existing records.
 * @param schemaTimeZone {string} the schema time zone
 *
 * @returns A record object ready to be sent to the backend.
 */
export function createPatchData(
  formId: number,
  status: string,
  data: Record<string, unknown>,
  dirtyFields: Record<string, unknown>
) {
  const record: RecordResult = {
    formId,
    status,
    fields: {},
  };

  const _dirtyFieldKeys = Object.keys(dirtyFields).filter((key) => key !== 'attachments');
  const recordObject = _createRecordObject(record, data, _dirtyFieldKeys);

  const patchData = {
    ...recordObject,
    files: (data.files as string[]) ?? [],
    images: (data.images as string[]) ?? [],
  };

  if (data.deletedFiles && Array.isArray(data.deletedFiles)) {
    // include the 'deletedFiles' in the payload (https://myosh.atlassian.net/browse/MYOSH-6113?focusedCommentId=53410)
    patchData.deletedFiles = data.deletedFiles;
  }

  if (data.attachmentConfirmation) {
    patchData.attachmentConfirmation = data.attachmentConfirmation;
  }

  return patchData;
}

function _createRecordObject(record: RecordResult, data: Record<string, unknown>, dataKeys: string[]) {
  const hierarchyKeys = data.hierarchies ? Object.keys(forceAssert<Record<string, unknown>>(data.hierarchies)) : [];
  for (let i = 0, length = dataKeys.length; i < length; i++) {
    const dataKey = dataKeys[i];
    const dataItem = data[dataKey];

    if (dataKey === 'hierarchyValues') {
      record.hierarchies = _createHierarchiesObject(dataItem as Record<string, HierarchyDropdownValue>);
    } else if (isInstanceOfValueTextType(dataItem)) {
      const textType = forceAssert<ValueTextType>(dataItem);
      record.fields[dataKey] = {
        id: typeof textType.value === 'string' ? parseInt(textType.value) : textType.value,
        value: textType.text,
      };
    } else if (
      Array.isArray(dataItem) &&
      dataItem.length > 0 &&
      (isInstanceOfOptionBuilder(dataItem[0]) || isInstanceOfValueTextType(dataItem[0]))
    ) {
      const dataArray = dataItem;
      record.fields[dataKey] = [];
      for (let j = 0; j < dataArray.length; j++) {
        const dataArrayItem = dataArray[j];
        if (isInstanceOfOptionBuilder(dataArrayItem)) {
          forceAssert<Array<OptionBuilderValue>>(record.fields[dataKey])[j] = {
            id:
              typeof dataArrayItem.value === 'string' ? parseInt(dataArrayItem.value) : (dataArrayItem.value as number),
            value: dataArrayItem.text,
            position: dataArrayItem.position ?? 0,
            archivedOption: dataArrayItem.archivedOption ?? false,
            defaultOption: dataArrayItem.defaultOption ?? false,
          };
        } else if (isInstanceOfValueTextType(dataArrayItem)) {
          forceAssert<Array<DropDownValue>>(record.fields[dataKey])[j] = {
            id: typeof dataArrayItem.value === 'string' ? parseInt(dataArrayItem.value) : dataArrayItem.value,
            value: dataArrayItem.text,
          };
        }
      }
    } else if (dataItem instanceof Date) {
      record.fields[dataKey] = formatToDateOnly(dataItem);
    } else if (isDropDownNoSelection(dataItem)) {
      record.fields[dataKey] = null;
    } else if (!hierarchyKeys.some((key) => dataKey === key)) {
      record.fields[dataKey] = dataItem;
    }
  }

  return record;
}

function _createHierarchiesObject(hierarchies: Record<string, HierarchyDropdownValue>) {
  const transformedObject: Record<string, string> = {};

  for (const key in hierarchies) {
    if (Object.hasOwn(hierarchies, key) && hierarchies[key]) {
      transformedObject[key] = hierarchies[key].caption || '';
    }
  }

  return transformedObject;
}

/**
 * Represents a single history field update used during record synchronization.
 *
 * This interface is the minimal key/value pair form expected by the backend when submitting updated history fields.
 */
export interface HistoryFieldType {
  key: number | string;
  value?: string;
}

/**
 * Extracts and transforms history field values for form submission.
 *
 * If a 'draft' is not found, the history field is removed from the record later on in `transformHistoryFieldsValue`, as there are no updates.
 *
 * If a 'draft' is found, the new comment object is modified and attached to the data field in a format expected by the component.
 * This is the same format in which we receive the data from the REST endpoint for a record.
 *
 * Furthermore, the key/value pair is returned from the method so it can be added to the record data. This step is needed
 * as the REST endpoint expect a new value of the history field to just be the 'string' of the new message.
 *
 * @param fields the form fields configuration
 * @param data the record data. **NOTE: This parameter is mutated** - draft comments are updated with user/date fields.
 *
 * @returns an array of objects with key/value pair of the 'new' history value or an empty array
 */
export function extractHistoryFields(fields: Array<DynamicFormField>, data: JsonDataItem) {
  const historyFields: Array<HistoryFieldType> = [];
  const dataKeys = Object.keys(data);

  for (const key of dataKeys) {
    const field = findFieldRecursiveSearch(
      fields,
      (item: DynamicFormField) => item.fieldType === 'HISTORY' && item.id.toString() === key
    );

    if (field) {
      // Clone the comment array to avoid mutating it during transformation
      const _value = structuredClone(data[field.id]) as CommentElementProps[];

      if (_value?.[0]?.draft === true) {
        // return the key/value pair for submission with the update/save of the record
        historyFields.push({
          key: field.id,
          value: _value[0].message,
        });
      } else {
        historyFields.push({
          key: field.id,
        });
      }
    }
  }

  return historyFields;
}

/**
 * Transforms the history field value representation in the record to be saved.
 *
 * @param recordData The record data that is to be submitted to the backend
 * @param historyFields The array of history field pairs extracted by calling `extractHistoryFields`.
 * @returns The record data with a correctly formatted history field value.
 */
export function transformHistoryFieldsValue(recordData: RecordResult, historyFields: Array<HistoryFieldType>) {
  let dataToSave = recordData;

  if (historyFields.length > 0) {
    for (let i = 0; i < historyFields.length; i++) {
      if (historyFields[i].value) {
        // The history field expects a simple key/value pair when adding a new comment
        dataToSave = {
          ...recordData,
          fields: { ...dataToSave.fields, [historyFields[i].key]: historyFields[i].value },
        };
      } else {
        // Remove the history field from the data to be sent to the backend
        delete dataToSave.fields[historyFields[i].key];
      }
    }
  }

  return dataToSave;
}

/**
 * Builds a map of TIMEFIELD field IDs for efficient lookup.
 *
 * @param fields All dynamic form fields.
 * @returns Set of TIMEFIELD field IDs as strings.
 */
function buildTimeFieldIdSet(fields: Array<DynamicFormField>): Set<string> {
  const timeFieldIds = new Set<string>();

  function collectTimeFields(fieldList: Array<DynamicFormField>) {
    for (const field of fieldList) {
      if (field.fieldType === 'TIMEFIELD') {
        timeFieldIds.add(field.id.toString());
      }
      if (field.fields) {
        collectTimeFields(field.fields);
      }
    }
  }

  collectTimeFields(fields);
  return timeFieldIds;
}

/**
 * Transforms time field values to the correct HH:mm format required by the backend.
 * The GET default value endpoint may return time values in extended format (e.g., T02:45:03.000+08:00),
 * but the backend expects time values in HH:mm format only (e.g., 02:45).
 *
 * @param recordData The record data that is to be submitted to the backend
 * @param fields The form field definitions to identify TIMEFIELD types
 * @returns The record data with correctly formatted time field values
 */
export function transformTimeFieldsValue(recordData: RecordResult, fields: Array<DynamicFormField>) {
  // Build time field ID set once for O(m) instead of O(n*m) repeated searches
  const timeFieldIds = buildTimeFieldIdSet(fields);
  const fieldKeys = Object.keys(recordData.fields);
  const transformedFields: Record<string, unknown> = {};
  let hasChanges = false;

  for (const key of fieldKeys) {
    // O(1) lookup instead of O(m) recursive search
    if (timeFieldIds.has(key) && recordData.fields[key]) {
      const timeValue = recordData.fields[key] as string;
      // Convert from extended format (T02:45:03.000+08:00 or T02:45:00) to HH:mm format
      if (typeof timeValue === 'string' && timeValue.startsWith('T')) {
        // Extract HH:mm using regex to ensure valid format
        const timeMatch = timeValue.match(/^T(\d{2}:\d{2})/);
        if (timeMatch) {
          transformedFields[key] = timeMatch[1]; // Extract the HH:mm portion
          hasChanges = true;
        }
        // If format doesn't match, leave unchanged to trigger backend validation
      }
      // If already in correct format (HH:mm), leave as-is
    }
  }

  // Follow transformHistoryFieldsValue pattern: only create new object if changes were made
  if (hasChanges) {
    return {
      ...recordData,
      fields: { ...recordData.fields, ...transformedFields },
    };
  }

  return recordData;
}

/**
 * Collects IDs of fields of type RECORDLINK whose IDs appear as keys in the provided data object.
 *
 * @param fields All dynamic form fields.
 * @param data Form data.
 *
 * @returns Array of matched RECORDLINK field IDs.
 */
export function extractRecordlinkFieldIds(fields: Array<DynamicFormField>, data: Record<string, unknown>) {
  const linkedRecordFieldIds: Array<string> = [];
  const dataKeys = Object.keys(data);
  for (const key of dataKeys) {
    const field = findFieldRecursiveSearch(
      fields,
      (item: DynamicFormField) =>
        item.fieldType === 'CUSTOM' && item.customFieldType === 'RECORDLINK' && item.id.toString() === key
    );
    if (field) {
      linkedRecordFieldIds.push(field.id as string);
    }
  }
  return linkedRecordFieldIds;
}

/**
 * Normalizes record link field values by replacing objects with an `id` property
 * with their numeric IDs for the specified field identifiers. Mutates the input
 * record's `fields` in place and returns the same reference.
 *
 * @param recordData The record whose link field values will be transformed.
 * @param recordLinkFieldIds Array of field IDs that should be processed.
 *
 * @returns The same `recordData` instance after in-place transformation.
 */
export function transformRecordlinkFieldsValue(recordData: RecordResult, recordLinkFieldIds: Array<string>) {
  const dataToSave = recordData;

  if (recordLinkFieldIds.length > 0) {
    for (let i = 0; i < recordLinkFieldIds.length; i++) {
      const fieldValue = recordData.fields[recordLinkFieldIds[i]];
      if (fieldValue && Array.isArray(fieldValue)) {
        dataToSave.fields[recordLinkFieldIds[i]] = fieldValue.map((item) => {
          if (item && typeof item === 'object' && 'id' in item) {
            return String(item.id); // API expects string IDs
          }
          return item;
        });
      }
    }
  }

  return dataToSave;
}

/**
 * Finds the merge conflicts in the given record.
 *
 * This method will comapre the locally changed fields with the values from the remote record and compile a list of merge conflicts.
 *
 * @param initialServerRecord The initial record from the server that the user has locally modified.
 * @param localRecord The local record that the user is currently working with.
 * @param serverRecord The latest record as stored on the server, against which we are comapring.
 * @param dirtyFields The list of fileds the user has locally modified.
 * @param formSettings The form settings of the form the record belongs to.
 *
 * @returns A list of conflicts.
 */
export function findMergeConflicts(
  initialServerRecord: RecordResult,
  localRecord: RecordResult,
  serverRecord: RecordResult,
  dirtyFields: JsonDataItem,
  formSettings: ExtendedDynamicFormSettings
) {
  const fieldKeys = Object.keys(dirtyFields);
  const conflicts: Conflict[] = [];

  for (let i = 0; i < fieldKeys.length; i++) {
    const fieldKey = fieldKeys[i];

    if (fieldKey === 'hierarchies') {
      const isLocalHierarchyValueDifferentFromServerHierarchyValue = !isEqual(
        localRecord.hierarchies,
        serverRecord.hierarchies
      );

      const isInitialServerHierarchyValueDifferentFromServerHierarchyValue = !isEqual(
        initialServerRecord.hierarchies,
        serverRecord.hierarchies
      );

      if (
        isLocalHierarchyValueDifferentFromServerHierarchyValue &&
        isInitialServerHierarchyValueDifferentFromServerHierarchyValue
      ) {
        const conflict: Conflict = {
          id: 'hierarchies',
          caption: i18next.t('hierarchy'),
          type: 'CUSTOM',
          customFieldType: 'HIERARCHY',
          theirValue: serverRecord.hierarchies,
          myValue: localRecord.hierarchies,
          dynamicProperties: {
            hierarchySettings: { ...formSettings?.hierarchySettings } as ExtendedDynamicFormHierarchySettings,
          },
        };
        conflicts.push(conflict);
      }
    } else {
      const isLocalRecordValueDifferentFromServerRecordValue = !isEqual(
        localRecord.fields[fieldKey],
        serverRecord.fields[fieldKey]
      );

      // If the initial server record has the same value as the server record,
      // there is no conflict and the API has only reproted so due to time stamp mismatch.
      const isInitialServerRecordValueDifferentFromServerRecordValue = !isEqual(
        initialServerRecord.fields[fieldKey],
        serverRecord.fields[fieldKey]
      );

      if (
        isLocalRecordValueDifferentFromServerRecordValue &&
        isInitialServerRecordValueDifferentFromServerRecordValue
      ) {
        // this is a conflict
        const field = findFieldRecursive(formSettings.fields, fieldKey);
        if (field && 'HISTORY' !== field.fieldType) {
          // The questionnaire field needs special handling for the files/images which are part of the more details
          if (
            'TEXTUAL_QUESTIONNAIRE' === (field.customFieldType as CombinedFieldType) ||
            'NUMERIC_QUESTIONNAIRE' === (field.customFieldType as CombinedFieldType)
          ) {
            const myValue = (localRecord.fields[fieldKey] as QuestionnaireFieldValue).value;
            const theirValue = (serverRecord.fields[fieldKey] as QuestionnaireFieldValue).value;
            const iLength = myValue.length;
            for (let i = 0; i < iLength; i++) {
              const myValueItem = myValue[i];
              const jLength = theirValue.length;
              for (let j = 0; j < jLength; j++) {
                const theirValueItem = theirValue[j];
                if (myValueItem.questionId === theirValueItem.questionId) {
                  // overwrite local values as server values are always up to date
                  myValueItem.files = [...(theirValueItem.files ?? [])];
                  myValueItem.images = [...(theirValueItem.images ?? [])];
                }
              }
            }
          }

          const conflict: Conflict = {
            id: field.id,
            caption: field.caption,
            type: field.customFieldType as CombinedFieldType,
            theirValue: serverRecord.fields[fieldKey],
            myValue: localRecord.fields[fieldKey],
            dynamicProperties: field.dynamicProperties,
          };
          conflicts.push(conflict);
        }
      }
    }
  }

  return conflicts;
}

export function cleanupRecordData(data: JsonDataItem) {
  //the API throws an error if it finds an property that doesn't exist,  instead of just ignoring it, so we have to do this
  if (data.attachments) {
    delete data.attachments;
  }
  if (isObject(data.fields) && data.fields.attachments) {
    delete data.fields.attachments;
  }
}

export function resolveRecordStatus(status?: string, defaultStatus?: string) {
  return status || defaultStatus || 'Open';
}

/**
 * Finds the first dynamic field that matches the predicate.
 * @param {Array<DynamicFormField>} fields - The recursive list of fields to search.
 * @param {(field: DynamicFormField) => boolean} predicate - A function called to determine if a field is a match.
 * @returns {DynamicFormField | undefined} The first matching dynamic field or undefined if nothing is found.
 */
export function findFieldRecursiveSearch(
  fields: Array<DynamicFormField>,
  predicate: (field: DynamicFormField) => boolean
): DynamicFormField | undefined {
  for (let i = 0, length = fields.length; i < length; i++) {
    const field = fields[i];
    if (predicate(field)) {
      return field;
    }
    if (field.fields) {
      const result = findFieldRecursiveSearch(field.fields, predicate);
      if (result) {
        return result;
      }
    }
  }
}

/**
 * Builds the array of workflow step visibility conditions.
 *
 * @param activeWorkflowStep the current workflow step.
 *
 * @returns the workflow step visibility conditions.
 */
export function buildWorkflowStepVisibilityConditions(activeWorkflowStep?: ExtendedDynamicFormWorkflowStep) {
  const workflowStepVisibilityConditions: Array<VisibilityConditionConfig> = [];
  if (activeWorkflowStep && activeWorkflowStep.nextSteps) {
    for (let i = 0, length = activeWorkflowStep.nextSteps.length; i < length; i++) {
      const nextStep = activeWorkflowStep.nextSteps[i];
      if (nextStep.visibilityConditions) {
        workflowStepVisibilityConditions.push({
          id: `${nextStep.id}_${i}`,
          groups: [
            {
              conditions: nextStep.visibilityConditions,
            },
          ],
        });
      }
    }
  }
  return workflowStepVisibilityConditions;
}

/**
 * Adds the person field or user groups access control conditions as visibility conditions.
 *
 * This is performed to ensure that dynamic field changes are correctly applied.
 * Please note that this method **mutates** the supplied
 * `workflowStepVisibilityConditions` array.
 *
 * @param workflowStepVisibilityConditions The current workflow step visibility conditions.
 * @param workflowSteps The form workflow steps.
 * @param activeWorkflowStep The currently active workflow step.
 * @param userId The current user id.
 * @returns {Array<VisibilityConditionConfig>} the mutated `workflowStepVisibilityConditions` array of visibility conditions.
 */
export function addAccessControlConditionsAsVisibilityConditions(
  workflowStepVisibilityConditions: Array<VisibilityConditionConfig>,
  workflowSteps: Array<FormWorkflowStepAccessControl>,
  activeWorkflowStep: ExtendedDynamicFormWorkflowStep,
  userId: number
) {
  const workflowStep = workflowSteps.find((step) => step.id === activeWorkflowStep.id);
  if (workflowStep && activeWorkflowStep && activeWorkflowStep.nextSteps) {
    const accessControlVisibilityConditions: Array<VisibilityCondition> = [];
    const workflowStepAccessControlConfig = workflowStep.configs.find((step) => step.type === 'progress');

    // add the person field conditions
    if (workflowStepAccessControlConfig?.personFields) {
      for (let i = 0, length = workflowStepAccessControlConfig.personFields.length; i < length; i++) {
        const personField = workflowStepAccessControlConfig.personFields[i];

        const personCondition: VisibilityCondition = {
          id: personField.id,
          sourceFieldId: personField.id,
          fieldVisibilityType: 'FIELD',
          targetValueType: 'ENTITY_ID',
          targetValueId: userId,
          negation: false,
          order: i + 1,
          conjugate: 'OR',
          filterOperator: 'EQUALS',
        };

        accessControlVisibilityConditions.push(personCondition);
      }
    }

    // add the groups conditions (otherwise dynamic data chages might remove a workflow button)
    if (workflowStepAccessControlConfig?.roles) {
      const previousTotal = accessControlVisibilityConditions.length;

      for (let j = 0, jLength = workflowStepAccessControlConfig.roles.length; j < jLength; j++) {
        const role = workflowStepAccessControlConfig.roles[j];

        const visibilityCondition: VisibilityCondition = {
          id: role.id,
          fieldVisibilityType: 'GROUP',
          targetValueId: role.id,
          negation: false,
          order: previousTotal + j + 1,
          conjugate: 'OR',
          filterOperator: 'EQUALS',
        };

        accessControlVisibilityConditions.push(visibilityCondition);
      }
    }

    // combine any access control conditions to the existing visibility conditions
    if (accessControlVisibilityConditions.length > 0) {
      // this adds access control visibility conditions for each next step workflow button
      for (let k = 0, kLength = activeWorkflowStep.nextSteps.length; k < kLength; k++) {
        const nextStep = activeWorkflowStep.nextSteps[k];
        const conditionId = nextStep.id;
        if (workflowStepVisibilityConditions.length > 0) {
          const workflowStepVisibilityCondition = workflowStepVisibilityConditions.find(
            (condition) => condition.id.toString() === `${conditionId}_${k}`
          );

          if (workflowStepVisibilityCondition) {
            workflowStepVisibilityCondition.groups = [
              {
                conditions: [...accessControlVisibilityConditions],
              },
              ...workflowStepVisibilityCondition.groups,
            ];
          } else {
            workflowStepVisibilityConditions.push({
              id: `${conditionId}_${k}`,
              groups: [
                {
                  conditions: accessControlVisibilityConditions,
                },
              ],
            });
          }
        } else {
          workflowStepVisibilityConditions.push({
            id: `${conditionId}_${k}`,
            groups: [
              {
                conditions: accessControlVisibilityConditions,
              },
            ],
          });
        }
      }
    }
  }

  return workflowStepVisibilityConditions;
}

export const constructRecordTitle = (
  formName?: string,
  docNo?: number | string,
  status?: string,
  title?: string
): string => {
  return title ?? `${formName} - #${leftPadWithZeros(docNo)} - ${status}`;
};

export function constructRecordTitleElement(
  formName?: string,
  docNo?: number | string,
  status?: string,
  title?: string
): React.ReactNode {
  if (title) return title;

  return (
    <Tooltip
      tooltipClassName="bg-mono-1 whitespace-nowrap"
      description={formName}
      debounceTime={200}
      tooltipZIndexCheck
    >
      <span className="inline-flex max-w-full items-baseline gap-1 overflow-hidden">
        <span className="max-w-[70%] overflow-hidden truncate text-ellipsis whitespace-nowrap">{formName}</span>
        {docNo ? <span className="shrink-0"> - #{leftPadWithZeros(docNo)} - </span> : null}
        <span className="text-green-600 shrink-0">{status}</span>
      </span>
    </Tooltip>
  );
}
const leftPadWithZeros = (num?: number | string): string => {
  return num?.toString().padStart(6, '0') ?? '000000';
};

export const errorMessage = (response: RecordMutationErrorResponse) => {
  const responseErrors = response.data.validation?.responseErrors;
  return responseErrors && responseErrors[0].message && responseErrors[0].message !== 'null'
    ? responseErrors[0].message
    : i18next.t('record-save-failed');
};
